import * as numbers from "common/numbers";
import * as exposures from "domain/exposures";
import * as _ from "lodash";
import { useSelector } from "react-redux";
import * as rateChangeStore from "store/rateChange";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";

export const calculateExposureChanges = ({
  rateChange,
  layerRateChange,
  currentExposureData,
  priorExposureData,
  exposureList,
  inceptionYear,
  primaryExposure,
}) => {
  const components = (exposureList ?? [])
    .map((exposure) => {
      const key = exposure.key;
      return {
        key,
        exposure,
        selected: !!layerRateChange?.selectedExposures?.[key],
        priorValue: rateChange?.usePriorYearExposuresFromCurrentSubmission
          ? exposures.latestExposure(currentExposureData, key, {
              asAtYear: inceptionYear - 1,
            })
          : exposures.latestExposure(priorExposureData, key),
        currentValue: exposures.latestExposure(currentExposureData, key),
        deltaPctOverride: numbers.parseFloatOrNull(
          layerRateChange?.exposureOverrides?.[key]
        ),
        isPrimaryExposure: key === primaryExposure,
      };
    })
    .filter(
      (component) =>
        component.priorValue != null && component.currentValue != null
    )
    .map((component) => ({
      ...component,
      deltaPct:
        component.deltaPctOverride ??
        100 * (component.currentValue / component.priorValue - 1),
    }));

  const totalOverride = numbers.parseFloatOrNull(
    layerRateChange?.exposureOverrides?.["TOTAL"]
  );

  const totalRatio =
    totalOverride != null
      ? 1 + totalOverride / 100
      : components
          .filter((component) => component.selected)
          .reduce(
            (total, component) =>
              total == null || component.deltaPct == null
                ? null
                : total * (1 + component.deltaPct / 100),
            1
          );

  return {
    components,
    totalRatio,
  };
};

export const useActiveLayerExposureChanges = () => {
  const rateChange = useSelector(rateChangeStore.selectRateChange);
  const layerRateChange = useSelector(
    rateChangeStore.selectActiveLayerRateChange
  );
  const currentExposureData = useSelector(exposureSelectors.getData);
  const priorExposureData = useSelector((state) =>
    exposureSelectors.getData(stateSelectors.selectPriorState(state))
  );
  const exposureList = useSelector(exposureSelectors.getExposureList);
  const inceptionYear = useSelector(programSelectors.getInceptionYear);

  const primaryExposure = useSelector(exposureSelectors.getPrimaryExposure);

  return calculateExposureChanges({
    rateChange,
    layerRateChange,
    currentExposureData,
    priorExposureData,
    exposureList,
    inceptionYear,
    primaryExposure,
  });
};

export const isUsingLatestDefaults = ({ rateChange, defaults, config }) => {
  if (!defaults) {
    return true;
  }
  for (const [key, value] of Object.entries(defaults ?? {})) {
    if (!_.isEqual(rateChange?.[key], value)) {
      return false;
    }
  }
  const ignoredLayerKeys = config?.ignoredLayerKeys ?? [];
  for (const layer of Object.values(rateChange?.byLayer ?? {})) {
    for (const [key, value] of Object.entries(layer ?? {})) {
      if (
        !rateChangeStore.dontCopyLayerStates.includes(key) &&
        !ignoredLayerKeys.includes(key) &&
        !_.isEqual(rateChangeStore.defaultLayerState[key], value)
      ) {
        return false;
      }
    }
  }
  return true;
};

export const migrateToLatestDefaults = ({ rateChange, defaults }) => {
  return {
    ...defaults,
    byLayer: Object.fromEntries(
      Object.entries(
        rateChange?.byLayer ?? {}
      ).map(([layerId, layerValues]) => [
        layerId,
        Object.fromEntries(
          Object.entries(layerValues ?? {}).filter(([key, _]) =>
            ["selectedPriorLayerId"].includes(key)
          )
        ),
      ])
    ),
    activeLayerId: rateChange?.activeLayerId,
  };
};
